import { PALLET_LENGTH_IN_CENTIMETER, PALLET_MIN_WIDTH_IN_CENTIMETER } from 'constants/pallet'
import { Decorator } from 'final-form'
import createDecorator from 'final-form-calculate'
import get from 'lodash/get'
import isEqual from 'lodash/isEqual'
import {
  RadioSwitchValue,
  TOrderCreateCargoForm,
  TOrderCreateCustomer,
  TOrderCreateFormData,
  TOrderCreatePartnerFormData
} from 'modules/order/types/orderCreateTypes'
import { OrderCargoServiceType, OrderCreateCargo, OrderCreatePayer, OrderItemClient } from 'schema'

import { createTerminalSyncDecorators, resetTerminalSyncState, TerminalSyncState } from '../../helpers/decorator'

let lastDeparturePartner: TOrderCreatePartnerFormData | undefined
let lastDestinationPartner: TOrderCreatePartnerFormData | undefined

const departureTerminalState: TerminalSyncState = {
  lastOperations: undefined,
  lastTerminalId: undefined
}

const destinationTerminalState: TerminalSyncState = {
  lastOperations: undefined,
  lastTerminalId: undefined
}

export function resetTerminalSyncStates() {
  resetTerminalSyncState(departureTerminalState)
  resetTerminalSyncState(destinationTerminalState)
}

export const decorators = [
  createDecorator({
    isEqual: (a, b) => a === b || !b,
    field: 'customer.type',
    updates: (value: any, _field: string, allValues?: TOrderCreateFormData) => {
      let updates = {}
      const { departurePartner, destinationPartner } = allValues || {}

      if (value && departurePartner?.isUser === RadioSwitchValue.yes) {
        updates = { ...updates, departurePartner: undefined }
      }

      if (value && destinationPartner?.isUser === RadioSwitchValue.yes) {
        updates = { ...updates, destinationPartner: undefined }
      }

      if (value) {
        updates = { ...updates, 'customer.customerId': undefined, payer: undefined }
      }

      return updates
    }
  }) as Decorator<TOrderCreateFormData>,
  createDecorator({
    isEqual: (a, b) => a === b || !b,
    field: 'customer.customerId',
    updates: (value: any, _field: string, allValues?: TOrderCreateFormData) => {
      let updates = {}
      const { departurePartner, destinationPartner } = allValues || {}

      if (value && departurePartner?.isUser === RadioSwitchValue.yes) {
        updates = { ...updates, departurePartner: undefined }
      }

      if (value && destinationPartner?.isUser === RadioSwitchValue.yes) {
        updates = { ...updates, destinationPartner: undefined }
      }

      if (value) {
        updates = { ...updates, payer: undefined }
      }

      return updates
    }
  }) as Decorator<TOrderCreateFormData>,
  createDecorator({
    field: 'customer.hasSpecialCargoLimits',
    updates: (
      value,
      name,
      allValues?: {
        cargo?: TOrderCreateCargoForm[]
        customer?: TOrderCreateCustomer
      }
    ) => {
      const { cargo, customer } = allValues || {}
      const updates = {}

      cargo?.forEach((cargoItem) => {
        if (cargoItem.type === OrderCreateCargo.type.PALLET && !customer?.hasSpecialCargoLimits) {
          cargoItem.palletWidth = PALLET_MIN_WIDTH_IN_CENTIMETER
        }
      })

      return updates
    }
  }) as Decorator<TOrderCreateFormData>,
  ...createTerminalSyncDecorators('departure', departureTerminalState),
  ...createTerminalSyncDecorators('destination', destinationTerminalState),
  createDecorator({
    field: /cargo\[\d+].type/,
    updates: (value, name, allValues) => {
      let updates = {}
      const [fieldName] = name.split('.')
      const palletLength = get(allValues, `${fieldName}.palletLength`)
      const palletWidth = get(allValues, `${fieldName}.palletWidth`)

      if (value === OrderCargoServiceType.Id.PALLET) {
        if (!palletLength) {
          updates = {
            ...updates,
            [name.replace('type', 'palletLength')]: PALLET_LENGTH_IN_CENTIMETER
          }
        }

        if (!palletWidth) {
          updates = { ...updates, [name.replace('type', 'palletWidth')]: PALLET_MIN_WIDTH_IN_CENTIMETER }
        }
      }

      return updates
    }
  }) as Decorator<TOrderCreateFormData>,
  createDecorator({
    isEqual: (a, b) => a === b || !b,
    field: 'departurePartner',
    updates: (value, field, allValues?: TOrderCreateFormData) => {
      const isPartnerHasDiff = !isEqual(lastDeparturePartner, allValues?.departurePartner)
      lastDeparturePartner = allValues?.departurePartner
      if (
        allValues &&
        allValues.payer?.role &&
        allValues.payer?.role === OrderCreatePayer.Role.DEPARTURE_PARTNER &&
        isPartnerHasDiff
      ) {
        return {
          payer: undefined
        }
      }

      return {}
    }
  }) as Decorator<TOrderCreateFormData>,
  createDecorator({
    isEqual: (a, b) => a === b || !b,
    field: 'destinationPartner',
    updates: (value, field, allValues?: TOrderCreateFormData) => {
      const isPartnerHasDiff = !isEqual(lastDestinationPartner, allValues?.destinationPartner)
      lastDestinationPartner = allValues?.destinationPartner
      if (
        allValues &&
        allValues.payer?.role &&
        allValues.payer?.role === OrderCreatePayer.Role.DESTINATION_PARTNER &&
        isPartnerHasDiff
      ) {
        return {
          payer: undefined
        }
      }

      return {}
    }
  }) as Decorator<TOrderCreateFormData>,
  createDecorator({
    field: 'departurePartner.type',
    updates: (value) => {
      if (value === OrderItemClient.type.INDIVIDUAL) {
        return {
          'departurePartner.companyId': undefined,
          'departurePartner.company': undefined
        }
      }

      return {}
    }
  }) as Decorator<TOrderCreateFormData>,
  createDecorator({
    isEqual: (a, b) => a === b || !b,
    field: 'departurePartner.isUser',
    updates: (value) => {
      let updates = {}

      if (value) {
        updates = { ...updates, payer: undefined }
      }

      return updates
    }
  }) as Decorator<TOrderCreateFormData>,
  createDecorator({
    field: 'destinationPartner.type',
    updates: (value) => {
      if (value === OrderItemClient.type.INDIVIDUAL) {
        return {
          'destinationPartner.companyId': undefined,
          'destinationPartner.company': undefined
        }
      }

      return {}
    }
  }) as Decorator<TOrderCreateFormData>,
  createDecorator({
    isEqual: (a, b) => a === b || !b,
    field: 'destinationPartner.isUser',
    updates: (value) => {
      let updates = {}

      if (value) {
        updates = { ...updates, payer: undefined }
      }

      return updates
    }
  }) as Decorator<TOrderCreateFormData>
]
